import { Link, router, usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { UserSidebar } from '@/components/layout/UserSidebar'
import type { SharedProps } from '@/types'

interface Props {
  continue_watching: {
    episode: { id: number; title: string; slug: string; thumbnail_url: string | null; duration: string }
    position: number
    percentage: number
  } | null
  stats: { watch_seconds: number; completed_count: number; favorites_count: number }
  favorites: { id: number; title: string; slug: string; thumbnail_url: string | null }[]
}

function formatWatchTime(seconds: number) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

export default function Index({ continue_watching, stats, favorites }: Props) {
  const { auth } = usePage<SharedProps>().props
  const user = auth.user!

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <UserSidebar active="/dashboard" />
      {/* Main */}
      <main className="flex-1 p-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user.name}</h1>
            <p className="text-gray-400 text-sm mt-1">Pick up where you left off or jump into a saved lesson.</p>
          </div>
          <div className="w-64">
            <form onSubmit={e => { e.preventDefault(); router.get('/search', { q: (e.currentTarget.elements.namedItem('q') as HTMLInputElement).value }) }}>
              <input
                name="q"
                placeholder="Search your learning..."
                className="w-full px-4 py-2 text-sm border border-brand/30 rounded-full focus:outline-none focus:ring-2 focus:ring-brand/20 text-brand placeholder:text-brand/50"
              />
            </form>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Continue watching */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 overflow-hidden">
            {continue_watching ? (
              <>
                <div className="aspect-video bg-rose-50 relative">
                  {continue_watching.episode.thumbnail_url && (
                    <img
                      src={continue_watching.episode.thumbnail_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-mono text-brand mb-1">Continue watching</p>
                    <h2 className="font-bold text-gray-900 text-lg">{continue_watching.episode.title}</h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {Math.floor(continue_watching.position / 60)}:{String(continue_watching.position % 60).padStart(2, '0')} watched • {continue_watching.percentage}% complete
                    </p>
                  </div>
                  <Link href={`/episodes/${continue_watching.episode.slug}`}>
                    <Button>Resume</Button>
                  </Link>
                </div>
              </>
            ) : (
              <div className="aspect-video flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <p className="font-medium text-gray-500 mb-2">No episodes watched yet</p>
                  <Link href="/episodes"><Button>Browse episodes</Button></Link>
                </div>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 flex flex-col justify-center">
            <h3 className="font-bold text-gray-900 text-lg mb-5">Learning Stats</h3>
            <div className="space-y-3 font-mono text-sm font-semibold text-gray-800">
              <p>{formatWatchTime(stats.watch_seconds)} watched</p>
              <p>{stats.completed_count} episodes completed</p>
              <p>{stats.favorites_count} saved lessons</p>
            </div>
          </div>
        </div>

        {/* Favorites */}
        {favorites.length > 0 && (
          <div className="mt-6 bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Saved lessons</h3>
            <ul className="space-y-2">
              {favorites.map(ep => (
                <li key={ep.id}>
                  <Link href={`/episodes/${ep.slug}`} className="text-sm text-gray-700 hover:text-brand hover:underline">
                    {ep.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  )
}
